import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearSampleDevelopmentWl,
  fetchSampleDevelopmentWlList,
  SampleDevelopmentWlState,
  SampleDevelopmentWlListFilter,
} from '../../../state/sampleDevelopmentWl';
import {
  PageContainer,
  PageTitle,
  TableLoading,
  PageButtonGroup,
  DateTimeDisplay,
  SortLabel,
} from '../../components';
import { useFilter, useTableSorting } from '../../hooks';
import { SampleDevelopmentStatusLabel } from '../../../constants/sampleDevelopmentStatus';
import SampleDevelopmentWlFilter from './SampleDevelopmentWlFilter';

const initialSampleDevelopmentWlListFilter: SampleDevelopmentWlListFilter = {
  'Status.equalsTo': '',
};

const SampleDevelopmentWlList: React.FC<RouteComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { history } = props;
  const [filters, setFilters] = useFilter<SampleDevelopmentWlListFilter>(
    initialSampleDevelopmentWlListFilter,
  );

  const [sort, setSort] = useTableSorting({
    orderBy: 'sampleDevNumber',
    direction: 'desc',
  });

  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });

  const {
    items: sampleDevelopmentWlList,
    totalItems,
    loading,
  } = useSelector<AppState, SampleDevelopmentWlState>(
    (state) => state.sampleDevelopmentWl,
  );

  const handleChangeFilter = (newFilter: SampleDevelopmentWlListFilter): void => {
    setFilters(newFilter);
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    dispatch(fetchSampleDevelopmentWlList(filters, { ...paging, sort }));
    return function cleanUp() {
      dispatch(clearSampleDevelopmentWl());
    };
  }, [dispatch, paging, filters, sort]);

  const addNewSampleDevelopmentWl = (): void => {
    history.push('/sampleDevelopmentWl/form');
  };

  const editSampleDevelopmentWl = (id: number): void => {
    history.push(`/sampleDevelopmentWl/form/${id}`);
  };

  return (
    <PageContainer>
      <PageTitle>Sample Development WL</PageTitle>
      <SampleDevelopmentWlFilter
        filters={filters}
        onChangeFilter={handleChangeFilter}
        initialFilter={initialSampleDevelopmentWlListFilter}
      />
      <PageButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={addNewSampleDevelopmentWl}
          startIcon={<AddIcon />}
        >
          New Sample Development WL
        </Button>
      </PageButtonGroup>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="sampleDevNumber">
                Sample Development Number
              </SortLabel>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Program</TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Block</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={3}>
            {sampleDevelopmentWlList.map((sampleDevelopmentWl, index) => (
              <TableRow key={sampleDevelopmentWl.id}>
                <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                <TableCell>{sampleDevelopmentWl.sampleDevNumber}</TableCell>
                <TableCell>
                  {SampleDevelopmentStatusLabel[sampleDevelopmentWl.status!]}
                </TableCell>
                <TableCell>{sampleDevelopmentWl.block!.program!.name}</TableCell>
                <TableCell>{sampleDevelopmentWl.block!.brand!.name}</TableCell>
                <TableCell>{sampleDevelopmentWl.block!.item}</TableCell>
                <TableCell>{sampleDevelopmentWl.block!.blockNo}</TableCell>
                <TableCell>
                  <DateTimeDisplay value={sampleDevelopmentWl.createdAt} />
                </TableCell>
                <TableCell>{sampleDevelopmentWl.createdBy}</TableCell>
                <TableCell>
                  <Tooltip title={'Edit'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => editSampleDevelopmentWl(sampleDevelopmentWl.id!)}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default SampleDevelopmentWlList;
